import { useCallback, useMemo } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import useFetch from 'shared/hooks/useFetch';
import { queryString } from 'shared/util/query';
import type { Filters, ResultData } from '@/domain';
import { apiListAdImageWrapper } from '@/utils/urlPrepender';

interface UseSearchResultsWithPricesRegularProps {
    searchId: string | null;
    effectiveFilters: Filters;
}

export const useSearchResultsWithPricesRegular = ({ searchId, effectiveFilters }: UseSearchResultsWithPricesRegularProps) => {
    const { apiExternalUrl, reactiveApiExternalUrl } = useConfig();

    const { destination, ...filtersWithoutDestination } = effectiveFilters;

    const { url, fetchKey, shouldPoll } = useMemo(() => {
        if (searchId) {
            const key = `${searchId}${queryString(filtersWithoutDestination)}`;
            return {
                url: `${reactiveApiExternalUrl}/reactive/page/${key}`,
                fetchKey: key,
                shouldPoll: true,
            };
        }
        return {
            url: `${apiExternalUrl}/list${queryString(filtersWithoutDestination)}`,
            fetchKey: queryString(filtersWithoutDestination),
            shouldPoll: false,
        };
    }, [searchId, apiExternalUrl, reactiveApiExternalUrl, filtersWithoutDestination]);

    const refreshInterval = useCallback((data: ResultData | undefined) => (shouldPoll && !data?.completed ? 1000 : 0), [shouldPoll]);

    const { data: rawData, error } = useFetch<ResultData>({
        url,
        isEnabled: Boolean(fetchKey),
        options: {
            refreshInterval,
            dedupingInterval: 0,
            revalidateOnFocus: false,
        },
    });

    const isCompleted = shouldPoll ? rawData?.completed === true : Boolean(rawData);
    const data = isCompleted && rawData ? apiListAdImageWrapper(rawData) : undefined;
    const loading = !rawData || (shouldPoll && !isCompleted);

    return { data, error, loading };
};
